import { createConsumer } from '@rails/actioncable'

interface WebSocketMessage {
  type: string
  file_upload_id?: number
  chat_session_id?: number
  message_id?: number
  status?: string
  requires_review?: boolean
  message?: string
  classification?: {
    document_type: string
    transaction_type: string
    confidence: number
    primary_account: {
      account_code: string
      account_name: string
      reasoning: string
    }
    requires_review: boolean
    review_reason?: string
    error?: string
  }
  extracted_data?: {
    vendor_or_customer: string
    total_amount: number
    document_number: string
    document_date: string
    line_items_count: number
  }
  timestamp?: string
}

interface WebSocketSubscription {
  unsubscribe: () => void
  perform: (action: string, data?: any) => void
}

export interface WebSocketServiceCallbacks {
  onConnected?: () => void
  onDisconnected?: () => void
  onFileProcessingComplete?: (data: WebSocketMessage) => void
  onError?: (error: Error) => void
  onSubscribed?: () => void
}

class WebSocketService {
  private cable: any = null
  private subscription: WebSocketSubscription | null = null
  private isConnected: boolean = false
  private callbacks: WebSocketServiceCallbacks = {}

  /**
   * Initialize WebSocket connection
   */
  connect(callbacks: WebSocketServiceCallbacks = {}) {
    this.callbacks = callbacks

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
      const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000'
      
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Create WebSocket URL - replace http with ws
      const wsUrl = baseUrl.replace(/^http/, 'ws') + `/cable?token=${token}`
      
      this.cable = createConsumer(wsUrl)
      
      console.log('WebSocket connecting to:', wsUrl)
      
      this.isConnected = true
      this.callbacks.onConnected?.()

    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      this.callbacks.onError?.(error as Error)
    }
  }

  /**
   * Subscribe to a chat session channel
   */
  subscribeToChatSession(chatSessionId: number): WebSocketSubscription | null {
    if (!this.cable) {
      console.error('WebSocket not connected')
      return null
    }

    try {
      this.subscription = this.cable.subscriptions.create(
        {
          channel: 'ChatSessionChannel',
          chat_session_id: chatSessionId
        },
        {
          connected: () => {
            console.log('Connected to ChatSessionChannel for session:', chatSessionId)
            this.callbacks.onConnected?.()
          },

          disconnected: () => {
            console.log('Disconnected from ChatSessionChannel')
            this.isConnected = false
            this.callbacks.onDisconnected?.()
          },

          received: (data: WebSocketMessage) => {
            console.log('WebSocket message received:', data)
            this.handleMessage(data)
          }
        }
      )

      return this.subscription

    } catch (error) {
      console.error('Failed to subscribe to chat session:', error)
      this.callbacks.onError?.(error as Error)
      return null
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: WebSocketMessage) {
    switch (data.type) {
      case 'subscribed':
        console.log('Successfully subscribed to chat session updates')
        this.callbacks.onSubscribed?.()
        break

      case 'file_processing_complete':
        console.log('File processing complete:', data)
        this.callbacks.onFileProcessingComplete?.(data)
        break

      case 'pong':
        console.log('Pong received:', data.timestamp)
        break

      default:
        console.log('Unknown message type:', data.type, data)
    }
  }

  /**
   * Send ping to server
   */
  ping() {
    if (this.subscription) {
      this.subscription.perform('receive', { action: 'ping' })
    }
  }

  /**
   * Unsubscribe from current chat session
   */
  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    this.unsubscribe()
    if (this.cable) {
      this.cable.disconnect()
      this.cable = null
    }
    this.isConnected = false
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): 'connected' | 'disconnected' | 'connecting' {
    if (!this.cable) return 'disconnected'
    if (this.isConnected) return 'connected'
    return 'connecting'
  }

  /**
   * Check if connected
   */
  isWebSocketConnected(): boolean {
    return this.isConnected
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService()
export default webSocketService